/**
 * One stage gate on a real project (2026-09-29): the checklist of documents
 * and decisions, file uploads, submit for approval, approve / send back.
 * Replaces a detail view that showed empty lists for real projects, a
 * "Document editor coming soon" panel, and buttons that only popped
 * notifications. Backend: app/services/stage_gates.py.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ArrowLeft, CheckCircle2, Download, FileText, Paperclip, Plus, Send, Trash2, Undo2 } from 'lucide-react'

import { Button } from '../ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { attachmentsService } from '../../services/attachments'
import { gateWorkflowService, type GateItem, type GatePayload } from '../../services/gateWorkflow'
import { apiErrorMessage } from '../../utils/apiError'

const GATE_STATUS: Record<string, string> = {
  pending: 'Not started', 'in-progress': 'In progress', 'in-review': 'Waiting for approval', completed: 'Approved',
}
const DOC_STATUS: Record<string, string> = { not_started: 'Not started', in_progress: 'In progress', completed: 'Done' }
const DECISION_STATUS: Record<string, string> = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' }

export function GateWorkspace({ projectId, gateId, onBack, onChanged }: {
  projectId: number
  gateId: number
  onBack: () => void
  onChanged?: () => void
}) {
  const { user } = useAuth()
  const isApprover = Boolean(user?.is_superuser || user?.roles?.some((r) => r.name === 'Organization Owner'))
  const [data, setData] = useState<GatePayload | null>(null)
  const [busy, setBusy] = useState(false)
  const [newDoc, setNewDoc] = useState('')
  const [newDecision, setNewDecision] = useState('')
  const [decisionNote, setDecisionNote] = useState('')
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploadFor, setUploadFor] = useState<GateItem | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await gateWorkflowService.items(projectId, gateId))
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load this gate.'))
    }
  }, [projectId, gateId])
  useEffect(() => { load() }, [load])

  const run = async (fn: () => Promise<GatePayload>, ok?: string) => {
    setBusy(true)
    try {
      setData(await fn())
      if (ok) toast.success(ok)
      onChanged?.()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'That didn’t work. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    const item = uploadFor
    setUploadFor(null)
    if (!file || !item) return
    setBusy(true)
    try {
      const att = await attachmentsService.upload({ entityType: 'project', entityId: projectId, file, purpose: 'gate_document' })
      setData(await gateWorkflowService.updateItem(projectId, gateId, item.id, {
        attachment_id: att.id, status: item.status === 'completed' ? 'completed' : 'in_progress',
      }))
      toast.success(`Attached ${file.name}.`)
      onChanged?.()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Upload failed.'))
    } finally {
      setBusy(false)
    }
  }

  const openFile = async (attachmentId: number) => {
    try {
      window.open(await attachmentsService.downloadUrl(attachmentId), '_blank', 'noopener')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not open the file.'))
    }
  }

  if (!data) return <p className="p-6 text-sm text-muted-foreground">Loading gate…</p>
  const { gate, items } = data
  const locked = gate.status === 'in-review' || gate.status === 'completed'
  const docs = items.filter((i) => i.kind === 'document')
  const decisions = items.filter((i) => i.kind === 'decision')
  const ready = docs.every((d) => d.status === 'completed') && decisions.every((d) => d.status === 'approved')
  const select = 'min-h-[44px] rounded-md border border-border bg-background px-2 text-sm text-foreground'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <input ref={fileInput} type="file" className="hidden" onChange={onFile} aria-label="Gate document file" />
      <button type="button" onClick={onBack} className="flex min-h-[44px] items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All gates
      </button>

      <header className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{gate.gate_number}</p>
            <h2 className="text-xl font-semibold text-foreground">{gate.name}</h2>
            {gate.description && <p className="mt-1 text-sm text-muted-foreground">{gate.description}</p>}
            <p className="mt-2 text-xs text-muted-foreground">
              Approver: {gate.approver || 'workspace admins'}{gate.due_date ? ` · due ${gate.due_date}` : ''}
              {gate.completed_date ? ` · approved ${gate.completed_date}` : ''}
            </p>
          </div>
          <div className="text-right">
            <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground">{GATE_STATUS[gate.status] ?? gate.status}</span>
            <p className="mt-2 text-2xl font-semibold text-foreground">{gate.progress}%</p>
          </div>
        </div>
        <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className="h-full bg-primary" style={{ width: `${gate.progress}%` }} />
        </div>
        {gate.decision_note && gate.status !== 'completed' && (
          <p role="note" className="mt-4 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm text-foreground">
            <strong>Sent back:</strong> {gate.decision_note}
          </p>
        )}
      </header>

      <section aria-label="Documents" className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 font-medium text-foreground">Documents ({gate.documents_completed}/{gate.documents_total} done)</h3>
        {docs.length === 0 && <p className="text-sm text-muted-foreground">No documents required yet.</p>}
        <ul className="divide-y divide-border">
          {docs.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <span className="flex min-w-[200px] flex-1 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-sm font-medium text-foreground">{d.name}</span>
                  {d.attachment_id && (
                    <button type="button" onClick={() => openFile(d.attachment_id!)} className="flex items-center gap-1 text-xs text-primary underline">
                      <Download className="h-3 w-3" /> {d.attachment_name ?? 'Attached file'}
                    </button>
                  )}
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <select aria-label={`Status of ${d.name}`} className={select} value={d.status} disabled={locked || busy}
                  onChange={(e) => run(() => gateWorkflowService.updateItem(projectId, gateId, d.id, { status: e.target.value }))}>
                  {Object.entries(DOC_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Button variant="outline" size="sm" className="min-h-[44px] gap-1" disabled={locked || busy}
                  onClick={() => { setUploadFor(d); fileInput.current?.click() }}>
                  <Paperclip className="h-4 w-4" /> {d.attachment_id ? 'Replace' : 'Attach'}
                </Button>
                <Button variant="ghost" size="sm" className="min-h-[44px]" disabled={locked || busy} aria-label={`Remove ${d.name}`}
                  onClick={() => run(() => gateWorkflowService.removeItem(projectId, gateId, d.id))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
        {!locked && (
          <form className="mt-3 flex gap-2" onSubmit={(e) => {
            e.preventDefault()
            if (newDoc.trim().length < 2) return
            run(() => gateWorkflowService.addItem(projectId, gateId, { kind: 'document', name: newDoc.trim() })).then(() => setNewDoc(''))
          }}>
            <input value={newDoc} onChange={(e) => setNewDoc(e.target.value)} placeholder="Add a required document"
              aria-label="New document" className="min-h-[44px] flex-1 rounded-md border border-border bg-background px-3 text-sm" />
            <Button type="submit" variant="outline" className="min-h-[44px] gap-1" disabled={busy}><Plus className="h-4 w-4" /> Add</Button>
          </form>
        )}
      </section>

      <section aria-label="Decisions" className="rounded-xl border border-border bg-card p-5">
        <h3 className="mb-3 font-medium text-foreground">Decisions ({gate.decisions_approved}/{gate.decisions_total} approved)</h3>
        {decisions.length === 0 && <p className="text-sm text-muted-foreground">No decisions required yet.</p>}
        <ul className="divide-y divide-border">
          {decisions.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <span className="min-w-[200px] flex-1">
                <span className="block text-sm font-medium text-foreground">{d.name}</span>
                {d.note && <span className="block text-xs text-muted-foreground">{d.note}</span>}
              </span>
              <span className="flex items-center gap-2">
                <select aria-label={`Outcome of ${d.name}`} className={select} value={d.status} disabled={locked || busy}
                  onChange={(e) => run(() => gateWorkflowService.updateItem(projectId, gateId, d.id, { status: e.target.value }))}>
                  {Object.entries(DECISION_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Button variant="ghost" size="sm" className="min-h-[44px]" disabled={locked || busy} aria-label={`Remove ${d.name}`}
                  onClick={() => run(() => gateWorkflowService.removeItem(projectId, gateId, d.id))}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </span>
            </li>
          ))}
        </ul>
        {!locked && (
          <form className="mt-3 flex gap-2" onSubmit={(e) => {
            e.preventDefault()
            if (newDecision.trim().length < 2) return
            run(() => gateWorkflowService.addItem(projectId, gateId, { kind: 'decision', name: newDecision.trim() })).then(() => setNewDecision(''))
          }}>
            <input value={newDecision} onChange={(e) => setNewDecision(e.target.value)} placeholder="Add a decision this gate needs"
              aria-label="New decision" className="min-h-[44px] flex-1 rounded-md border border-border bg-background px-3 text-sm" />
            <Button type="submit" variant="outline" className="min-h-[44px] gap-1" disabled={busy}><Plus className="h-4 w-4" /> Add</Button>
          </form>
        )}
      </section>

      <section aria-label="Approval" className="rounded-xl border border-border bg-card p-5">
        {gate.status === 'completed' ? (
          <p className="flex items-center gap-2 text-sm text-foreground"><CheckCircle2 className="h-5 w-5 text-success" /> This gate is approved.</p>
        ) : gate.status === 'in-review' ? (
          isApprover ? (
            <div className="space-y-3">
              <p className="text-sm text-foreground">Submitted for approval. Review the documents and decisions above.</p>
              <label className="block text-sm text-foreground">Note (required when sending back)
                <textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)}
                  className="mt-1 min-h-[80px] w-full rounded-md border border-border bg-background p-2 text-sm" />
              </label>
              <div className="flex flex-wrap gap-2">
                <Button className="min-h-[44px] gap-1" disabled={busy}
                  onClick={() => run(() => gateWorkflowService.decide(projectId, gateId, 'approved', decisionNote), 'Gate approved.')}>
                  <CheckCircle2 className="h-4 w-4" /> Approve gate
                </Button>
                <Button variant="outline" className="min-h-[44px] gap-1" disabled={busy || !decisionNote.trim()}
                  onClick={() => run(() => gateWorkflowService.decide(projectId, gateId, 'sent_back', decisionNote), 'Gate sent back.')}>
                  <Undo2 className="h-4 w-4" /> Send back
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Waiting for a workspace admin to approve.</p>
          )
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {ready ? 'Everything is in place.' : 'Finish every document and get every decision approved, then submit.'}
            </p>
            <Button className="min-h-[44px] gap-1" disabled={busy || !ready}
              onClick={() => run(() => gateWorkflowService.submit(projectId, gateId), 'Submitted for approval.')}>
              <Send className="h-4 w-4" /> Submit for approval
            </Button>
          </div>
        )}
      </section>
    </div>
  )
}
